import React from 'react';
import { useNavigate } from 'react-router-dom';

import classnames from 'classnames/bind';

import ROUTES from '@CONSTANTS/ROUTES.constant';
import GenerationSteps from '@CONSTANTS/GENERATION_STEPS.constant';

import { BrandStepData as LocationStepData } from '@REDUCERS';

import useDocumentGenerationSteps from '@HOOKS/store/useDocumentGenerationSteps';
import { useGetAllCountriesQuery } from '@SERVICES';

import Grid from '@COMPONENTS/SHARED/Grid';
import SquareButton from '@COMPONENTS/COMMON/buttons/SquareButton';
import DataLoader from '@COMPONENTS/SHARED/DataLoader';
import EmptyStateHint from '@COMPONENTS/SHARED/EmptyStateHint';
import SearchbarInput from '@COMPONENTS/COMMON/inputs/input/SearchInput';
import StepNavigation from '@COMPONENTS/COMMON/navigation/StepNavigation';

import styles from './Location.module.scss';

const cx: CX = classnames.bind(styles);

function LocationPage() {
    const {
        actions: documentGenerationStepsActions,
        services: documentGenerationStepsServices,
    } = useDocumentGenerationSteps(() => true);

    const {
        data: locationsData = [], isLoading, isError, error,
    } = useGetAllCountriesQuery();

    const [locationName, setLocationName] = React.useState('');

    const navigate = useNavigate();

    const locationStep = documentGenerationStepsServices.getStepData(GenerationSteps.LOCATION);
    const hasSelectedLocation = locationStep.isComplete;

    const filteredLocations = React.useMemo(() => {
        const lowerCaseLocationName = locationName.toLowerCase();
        return locationsData.filter(({ name }: { name: string }) => name.toLowerCase().includes(lowerCaseLocationName));
    }, [locationName, locationsData]);

    function handleClick(id: number, name: string) {
        const data: LocationStepData = {
            id,
            name,
        };

        documentGenerationStepsActions.apply({
            id: GenerationSteps.LOCATION,
            isComplete: true,
            data,
        });

        navigate(ROUTES.options.path);
    }

    function getHighlighted(id: number) {
        const step = documentGenerationStepsServices.getStepData(GenerationSteps.LOCATION);
        return (step.data as any)?.id === id;
    }

    const handleNext = () => {
        if (hasSelectedLocation) {
            navigate(ROUTES.options.path);
        }
    };

    return (
        <div className={cx('page')}>
            <div className={cx('brand-header')}>
                <h1>Location</h1>
                <div className={cx('brand-search')}>
                    <SearchbarInput
                        isLoading={false}
                        placeholder="Search locations..."
                        value={locationName}
                        onChange={(val: string) => {
                            setLocationName(val);
                        }}
                        onFocus={() => {}}
                        iconWrapper="icon-wrapper"
                        inputWrapper="input-wrapper"
                    />
                </div>
            </div>

            <DataLoader
                isLoading={isLoading}
                isError={isError}
                error={error}
            >
                {locationsData.length === 0 ? (
                    <EmptyStateHint
                        type="locations"
                        title="No Locations Found"
                        description="It looks like there are no locations configured yet. Locations help customize documents for specific geographic areas or facilities. You can add new locations in the admin settings."
                    />
                ) : (
                    <div>
                        <Grid>
                            {filteredLocations.map((location: any) => {
                                const highlighted = getHighlighted(location.id);
                                return (
                                    <SquareButton
                                        key={location.id}
                                        highlighted={highlighted}
                                        title={location.name}
                                        onClick={() => handleClick(location.id, location.name)}
                                    />
                                );
                            })}
                        </Grid>
                        
                        {filteredLocations.length === 0 && locationName && (
                            <div className={cx('no-results')}>
                                <p>No locations found matching &quot;{locationName}&quot;</p>
                                <p>Try adjusting your search terms.</p>
                            </div>
                        )}
                    </div>
                )}
            </DataLoader>

            <StepNavigation
                onNext={handleNext}
                nextDisabled={!hasSelectedLocation}
                showNext={hasSelectedLocation}
                nextText="Continue to Options"
                sticky
            />
        </div>
    );
}

export default LocationPage;
